import { useState, useEffect, useContext } from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import { Refresh, Search, Clear } from '@mui/icons-material';
import { UserContext } from '../../App';

import { getAuthToken } from '../../utils/cookieUtils';
// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Helper function to format dates with facility timezone
const formatDateTimeWithTimezone = (dateString: string | Date, timezone?: string): string => {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  // Default to UTC if no timezone provided
  const tz = timezone || 'UTC';

  try {
    // Format date and time
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const partsMap: Record<string, string> = {};
    parts.forEach(part => {
      partsMap[part.type] = part.value;
    });

    const formattedDate = `${partsMap.month}/${partsMap.day}/${partsMap.year} ${partsMap.hour}:${partsMap.minute}`;

    // Get timezone abbreviation
    const tzAbbr = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || tz;

    return `${formattedDate} ${tzAbbr}`;
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    return date.toLocaleString();
  }
};

// Interface for carrier trailer (flattened from distcomp)
interface CarrierViewItem {
  trailerUuid: string;
  trailerNumber: string;
  carrierId: string;
  trailerType?: string;
  trailerNotes?: string;
  trailerCreatedAt: string;
  trailerUpdatedAt: string;
  carrierName?: string;
  facilityId?: string;
  facilityName?: string;
  facilityConfiguration?: {
    timezone?: string;
    [key: string]: any;
  };
  lineageUuid?: string;
  isActive?: boolean;
  isRecentlyExited?: boolean;
  currentYardSpotUuid?: string;
  currentDockUuid?: string;
  currentFacilityZoneUuid?: string;
  lineageUpdatedAt?: string;
  yardSpotUuid?: string;
  yardSpotName?: string;
  yardSpotUpdatedAt?: string;
  dockUuid?: string;
  dockName?: string;
  facilityZoneUuid?: string;
  facilityZoneName?: string;
  statusName?: string;
  locationType?: string;
  locationName?: string;
  driverFirstName?: string;
  driverLastName?: string;
  shipmentReferenceNumber?: string;
  daCreatedAt?: string;
  daUpdatedAt?: string;
  daCheckOut?: string;
  checkOutTime?: string;
}

const CarrierTrailers = () => {
  // @ts-ignore
  const { user } = useContext(UserContext);
  const [trailers, setTrailers] = useState<CarrierViewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchTrailers = async () => {
    setLoading(true);
    setError(null);

    try {
      const authToken = getAuthToken();

      const query = `
        query {
          myCarrierTrailers {
            totalCount
            cachedAt
            items {
              trailerUuid
              trailerNumber
              carrierId
              trailerType
              refrigerated
              trailerNotes
              trailerCreatedAt
              trailerUpdatedAt
              carrierName
              facilityId
              facilityName
              facilityConfiguration
              lineageUuid
              isActive
              isRecentlyExited
              currentYardSpotUuid
              currentDockUuid
              currentFacilityZoneUuid
              lineageUpdatedAt
              yardSpotUuid
              yardSpotName
              yardSpotUpdatedAt
              dockUuid
              dockName
              facilityZoneUuid
              facilityZoneName
              statusName
              locationType
              locationName
              driverFirstName
              driverLastName
              shipmentReferenceNumber
              daCreatedAt
              daUpdatedAt
              daCheckOut
              checkOutTime
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ query })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      setTrailers(result.data.myCarrierTrailers?.items || []);
    } catch (err: any) {
      console.error('Error fetching trailers:', err);
      setError(err.message || 'Failed to fetch trailers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchTrailers();
    }
  }, [user]);

  // Get facility timezone from trailer
  const getFacilityTimezone = (trailer: CarrierViewItem): string | undefined => {
    const config = trailer.facilityConfiguration;
    if (typeof config === 'string') {
      try {
        const parsed = JSON.parse(config);
        return parsed.timezone;
      } catch {
        return undefined;
      }
    } else if (config && typeof config === 'object') {
      return config.timezone;
    }
    return undefined;
  };

  // Filter trailers based on search term with exclusive multi-word search
  const getFilteredTrailers = (trailers: CarrierViewItem[], searchTerm: string) => {
    if (!searchTerm.trim()) return trailers;

    // Split search term into individual words and filter out empty strings
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);

    return trailers.filter(trailer => {
      const driverName = [trailer.driverFirstName, trailer.driverLastName]
        .filter(Boolean)
        .join(' ');

      const searchableText = [
        trailer.trailerNumber,
        trailer.carrierName || '',
        trailer.statusName || '',
        trailer.shipmentReferenceNumber || '',
        trailer.locationName || '',
        trailer.facilityZoneName || '',
        trailer.facilityName || '',
        driverName
      ].join(' ').toLowerCase();

      // Check that ALL search words are found in the searchable text (exclusive AND)
      return searchWords.every(word => searchableText.includes(word));
    });
  };

  const filteredTrailers = getFilteredTrailers(trailers, searchTerm);

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            My Trailers
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search trailers, status, driver, shipment ref..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: { xs: 180, sm: 300 },
                maxWidth: { xs: 200, sm: 'none' }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <IconButton onClick={fetchTrailers} disabled={loading} color="primary">
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            <Typography>{error}</Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Trailer #</strong></TableCell>
                  <TableCell><strong>SCAC</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Facility</strong></TableCell>
                  <TableCell><strong>Current Location</strong></TableCell>
                  <TableCell><strong>Shipment #</strong></TableCell>
                  <TableCell><strong>Driver</strong></TableCell>
                  <TableCell><strong>Driver Check In Time</strong></TableCell>
                  <TableCell><strong>Driver Check Out Time</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                  <TableCell><strong>Trailer Arrived At</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTrailers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No trailers match your search' : 'No trailers found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTrailers.map((trailer) => (
                    <TableRow key={trailer.trailerUuid} hover>
                      <TableCell>{trailer.trailerNumber}</TableCell>
                      <TableCell>{trailer.carrierName || '-'}</TableCell>
                      <TableCell>
                        {trailer.statusName ? (
                          <Chip
                            label={trailer.statusName}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>{trailer.facilityName || '-'}</TableCell>
                      <TableCell>
                        {trailer.isRecentlyExited ? (
                          <Typography variant="body2">Recently Exited</Typography>
                        ) : trailer.locationName ? (
                          <Box>
                            <Typography variant="body2">
                              {trailer.locationName}
                            </Typography>
                            {trailer.locationType === 'YardSpot' && trailer.facilityZoneName && (
                              <Typography variant="caption" color="text.secondary">
                                {trailer.facilityZoneName}
                              </Typography>
                            )}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {trailer.shipmentReferenceNumber || '-'}
                      </TableCell>
                      <TableCell>
                        {trailer.driverFirstName || trailer.driverLastName ? (
                          <Typography variant="body2">
                            {`${trailer.driverFirstName || ''} ${trailer.driverLastName || ''}`.trim() || '-'}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {trailer.daCreatedAt ? (
                          <Typography variant="body2">
                            {formatDateTimeWithTimezone(
                              trailer.daCreatedAt,
                              getFacilityTimezone(trailer)
                            )}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {trailer.checkOutTime ? (
                          <Typography variant="body2">
                            {formatDateTimeWithTimezone(
                              trailer.checkOutTime,
                              getFacilityTimezone(trailer)
                            )}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={trailer.trailerNotes || ''}
                        >
                          {trailer.trailerNotes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTimeWithTimezone(
                            trailer.lineageUpdatedAt || trailer.trailerUpdatedAt,
                            getFacilityTimezone(trailer)
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {searchTerm
              ? `Showing ${filteredTrailers.length} of ${trailers.length} trailers`
              : `Total Trailers: ${trailers.length}`
            }
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default CarrierTrailers;
